"use client";
import React from "react";
import { Checkbox } from "components/ui/checkbox";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "components/ui/icon";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { cn } from "lib/utils";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "components/ui/button";
import { routes } from "router";
import { signInAction } from "action/auth-action";
import { useAuth } from "context/auth-context";

const schema = z.object({
  username: z.string({ message: "Your username is invalid." }),
  password: z
    .string()
    .min(4, { message: "Password must contain at least 4 characters" }),
});
const LoginForm = () => {
  // const [isPending, startTransition] = React.useTransition();
  const navigate = useNavigate();
  const [passwordType, setPasswordType] = React.useState("password");
  const [loading, setLoading] = React.useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [keepSignedIn, setKeepSignedIn] = React.useState(true);
  const { login } = useAuth();

  const togglePasswordType = () => {
    if (passwordType === "text") {
      setPasswordType("password");
    } else if (passwordType === "password") {
      setPasswordType("text");
    }
  };
  const {
    register,
    handleSubmit,
    // reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "all",
    defaultValues: {
      username: "",
      password: "",
    },
  });
  // const [isVisible, setIsVisible] = React.useState(false);

  // const toggleVisibility = () => setIsVisible(!isVisible);

  const onSubmit = (data: z.infer<typeof schema>) => {
    // startTransition(() => {
    setLoading(true);
    signInAction(data)
      .then((response) => {
        if (response.status === 200) {
          // Extract business info if present
          const businessInfo = response.body.business_id
            ? {
              businessId: response.body.business_id,
              businessDomain: response.body.business_domain || "",
              businessName: response.body.business_name || "",
            }
            : undefined;

          React.startTransition(() => {
            login(
              response.body.access_token,
              response.body.refresh_token,
              businessInfo,
            );
            navigate(routes.Home.path, { replace: true });
          });
          toast.success("Successfully logged in");
        } else {
          setLoading(false);
          toast.error(response.error);
        }
      })
      .catch((err: any) => {
        setLoading(false);
        toast.error(
          err?.name === "AbortError"
            ? "The server is taking too long to respond. Please try again in a moment."
            : "Failed to login, An error occurred",
        );
        console.error(err);
      });

    // });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-5 2xl:mt-7 space-y-4">
      <div className="space-y-2">
        <Label htmlFor="username" className="font-medium text-default-600">
          Username{" "}
        </Label>
        <Input
          size="lg"
          // disabled={loading}
          {...register("username")}
          type="username"
          id="username"
          className={cn("", {
            "border-destructive ": errors.username,
          })}
        />
      </div>
      {errors.username && (
        <div className=" text-destructive mt-2 text-sm">
          {errors.username.message}
        </div>
      )}

      <div className="mt-3.5 space-y-2">
        <Label htmlFor="password" className="mb-2 font-medium text-default-600">
          Password{" "}
        </Label>
        <div className="relative">
          <Input
            size="lg"
            // disabled={loading}
            {...register("password")}
            type={passwordType}
            id="password"
            className="peer  "
            placeholder=" "
          />

          <div
            className="absolute top-1/2 -translate-y-1/2 right-2 cursor-pointer"
            onClick={togglePasswordType}
          >
            {passwordType === "password" ? (
              <Icon icon="heroicons:eye" className="w-5 h-5 text-default-400" />
            ) : (
              <Icon
                icon="heroicons:eye-slash"
                className="w-5 h-5 text-default-400"
              />
            )}
          </div>
        </div>
      </div>
      {errors.password && (
        <div className=" text-destructive mt-2 text-sm">
          {errors.password.message}
        </div>
      )}

      <div className="flex justify-between">
        <div className="flex gap-2 items-center">
          <Checkbox
            id="checkbox"
            defaultChecked
            onCheckedChange={(value) =>
              setKeepSignedIn(value === "indeterminate" ? false : value)
            }
          />
          <Label htmlFor="checkbox">Keep Me Signed In</Label>
        </div>
        <Link
          to={routes.ForgotPassword.path}
          className="text-sm text-default-800 dark:text-default-400 leading-6 font-medium hover:underline"
        >
          Forgot password?
        </Link>
      </div>
      <Button fullWidth disabled={loading}>
        {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
        {loading ? "Signing in..." : "Sign In"}
      </Button>
    </form>
  );
};
export default LoginForm;
